import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SidebarMenus from '../components/SidebarMenus';
import Header from '../components/Header';
import api from '../utils/api';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import '../assets/styles/dashboard.css';
import '../assets/styles/settings.css';
import '../assets/styles/common.css';
import SweetAlert from '../components/SweetAlert';
import eyeIcon from '../assets/images/eye.png';
import eyeCloseIcon from '../assets/images/eyeClose.png';

const TABS = [
  { id: 'company', label: 'Company Details', icon: '🏢' },
  { id: 'contact', label: 'Contact Details', icon: '👤' },
  { id: 'billing', label: 'Billing Info', icon: '💳' },
  { id: 'password', label: 'Change Password', icon: '🔒' },
  { id: 'notifications', label: 'Notifications', icon: '🔔' },
];

const Settings = () => {
  const navigate = useNavigate();
  const agencyData = JSON.parse(localStorage.getItem('agencyData') || '{}');
  const partnerName = agencyData.companyName || 'Partner';
  const partnerEmail = agencyData.email || '';

  const initials = partnerName
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const [agencyFullDetails, setAgencyFullDetails] = useState(null);
  const [activeTab, setActiveTab] = useState('company');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});

  const [company, setCompany] = useState({
    companyName: '',
    registrationNumber: '',
    taxNumber: '',
    billingAddress: '',
    city: '',
    postalCode: '',
    country: '',
    agencyType: '',
  });

  const [contact, setContact] = useState({
    contactPerson: '',
    email: '',
    phone: '',
    jobTitle: '',
    website: '',
    additionalNotes: '',
  });

  const [billing, setBilling] = useState({
    bankName: '',
    accountName: '',
    accountNumber: '',
    routingNumber: '',
    paymentMethod: 'bank',
  });

  const [password, setPassword] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [notifPrefs, setNotifPrefs] = useState({
    emailBookings: true,
    emailCommissions: true,
    pushUpdates: true,
    smsAlerts: false,
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) navigate('/');
  }, [navigate]);

  const showMsg = (text) => {
    setMessage(text);
    setTimeout(() => setMessage(''), 3000);
  };

  const clearError = (name) => {
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleCompanyChange = (e) => {
    const { name, value } = e.target;
    setCompany((prev) => ({ ...prev, [name]: value }));
    clearError(name);
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContact((prev) => ({ ...prev, [name]: value }));
    clearError(name);
  };

  const handleBillingChange = (e) => {
    const { name, value } = e.target;
    setBilling((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPassword((prev) => ({ ...prev, [name]: value }));
    clearError(name);
  };

  const updateLocalAgency = (patch) => {
    const next = { ...(agencyFullDetails || agencyData), ...patch };
    setAgencyFullDetails(next);
    localStorage.setItem('agencyData', JSON.stringify(next));
    if (patch.companyName) localStorage.setItem('partnerName', patch.companyName);
    if (patch.email) localStorage.setItem('partnerEmail', patch.email);
  };

  // Same validation rules as Register step 1
  const validateCompany = () => {
    const newErrors = {};
    const companyName = company.companyName.trim();

    if (!companyName) {
      newErrors.companyName = 'Company name is required';
    } else if (companyName.length < 3) {
      newErrors.companyName = 'Company name must be at least 3 characters';
    } else if (companyName.length > 100) {
      newErrors.companyName = 'Company name is too long (maximum 100 characters)';
    } else if (/^(.)\1{5,}$/.test(companyName)) {
      newErrors.companyName = 'Company name cannot contain repeated characters';
    } else if (!/[a-zA-Z]/.test(companyName)) {
      newErrors.companyName = 'Company name must contain at least one letter';
    }

    if (!company.registrationNumber.trim()) {
      newErrors.registrationNumber = 'Registration number is required';
    } else if (company.registrationNumber.trim().length < 5) {
      newErrors.registrationNumber = 'Registration number must be at least 5 characters';
    }

    if (!company.taxNumber.trim()) {
      newErrors.taxNumber = 'Tax number is required';
    }

    if (!company.billingAddress.trim()) {
      newErrors.billingAddress = 'Billing address is required';
    } else if (company.billingAddress.trim().length < 10) {
      newErrors.billingAddress = 'Please enter a complete address';
    }

    if (!company.city.trim()) {
      newErrors.city = 'City is required';
    } else if (company.city.trim().length < 2) {
      newErrors.city = 'City name is too short';
    }

    if (!company.postalCode.trim()) {
      newErrors.postalCode = 'Postal code is required';
    } else if (!/^[A-Z0-9\s-]{3,10}$/i.test(company.postalCode.trim())) {
      newErrors.postalCode = 'Please enter a valid postal code';
    }

    if (!company.agencyType) {
      newErrors.agencyType = 'Please select an agency type';
    }

    if (!company.country.trim()) {
      newErrors.country = 'Country is required';
    } else if (company.country.trim().length < 2) {
      newErrors.country = 'Please enter a valid country name';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Same required contact rules as Register step 2
  const validateContact = () => {
    const newErrors = {};

    if (!contact.contactPerson.trim()) {
      newErrors.contactPerson = 'Contact person name is required';
    }

    if (!contact.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(contact.email)) {
      newErrors.email = 'Invalid email address';
    }

    if (!contact.phone) {
      newErrors.phone = 'Phone number is required';
    } else if (!isValidPhoneNumber(contact.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePassword = () => {
    const newErrors = {};

    if (!password.currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!password.newPassword) {
      newErrors.newPassword = 'Password is required';
    } else if (password.newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters';
    }

    if (!password.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm password';
    } else if (password.newPassword !== password.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getAgencyFullDetails = async () => {
    try {
      const res = await api.post('/api/website/agency-full-details', {
        agencyId: agencyData._id,
      });

      if (res?.data?.status === 1) {
        const data = res.data.data;
        setAgencyFullDetails(data);

        setCompany({
          companyName: data.companyName || '',
          registrationNumber: data.registrationNumber || '',
          taxNumber: data.taxNumber || '',
          billingAddress: data.billingAddress || '',
          city: data.city || '',
          postalCode: data.postalCode || '',
          country: data.country || '',
          agencyType: data.agencyType || '',
        });

        setContact({
          contactPerson: data.contactPerson || '',
          email: data.email || '',
          phone: data.phone || '',
          jobTitle: data.jobTitle || '',
          website: data.website || '',
          additionalNotes: data.additionalNotes || '',
        });

        setBilling({
          bankName: data.bankName || '',
          accountName: data.accountName || '',
          accountNumber: data.accountNumber || '',
          routingNumber: data.routingNumber || '',
          paymentMethod: data.paymentMethod || 'bank',
        });

        setNotifPrefs({
          emailBookings: data.emailBookings ?? true,
          emailCommissions: data.emailCommissions ?? true,
          pushUpdates: data.pushUpdates ?? true,
          smsAlerts: data.smsAlerts ?? false,
        });
      }
    } catch (err) {
      console.error('Failed to load agency details', err);
    }
  };

  useEffect(() => {
    getAgencyFullDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveProfile = async (sectionData) => {
    setSaving(true);
    try {
      const res = await api.post('/api/website/update-agency-profile', {
        agencyId: agencyData._id,
        ...sectionData,
      });

      if (res?.data?.status === 1) {
        updateLocalAgency(sectionData);
        SweetAlert.success('Changes saved successfully');
      } else {
        updateLocalAgency(sectionData);
        SweetAlert.error(res?.data?.message || 'Changes not saved successfully');
      }
    } catch (err) {
      console.error(err);
      updateLocalAgency(sectionData);
      SweetAlert.error('Changes not saved successfully');
    } finally {
      setSaving(false);
    }
  };

  const onSaveCompany = (e) => {
    e.preventDefault();
    if (!validateCompany()) return;
    handleSaveProfile(company);
  };

  const onSaveContact = (e) => {
    e.preventDefault();
    if (!validateContact()) return;
    handleSaveProfile(contact);
  };

  const onSaveBilling = (e) => {
    e.preventDefault();
    handleSaveProfile(billing);
  };

  const onSaveNotifications = (e) => {
    e.preventDefault();
    handleSaveProfile(notifPrefs);
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (!validatePassword()) return;

    setSaving(true);
    try {
      const res = await api.post('/api/website/update-agency-profile', {
        agencyId: agencyData._id,
        currentPassword: password.currentPassword,
        newPassword: password.newPassword,
      });

      if (res?.data?.status === 1) {
        setPassword({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setErrors({});
        SweetAlert.success('Password updated successfully');
      } else {
        SweetAlert.error(res?.data?.message || 'Failed to update password');
      }
    } catch (err) {
      console.error(err);
      SweetAlert.error('Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dashboard-layout">
      <SidebarMenus partnerName={partnerName} partnerEmail={partnerEmail} />

      <main className="dashboard-main">
        <Header
          title="Profile & Settings"
          search={true}
          bell={true}
          partnerName={partnerName}
          initials={initials}
          searchPlaceholder="Search bookings, customers..."
        />

        {message ? <div className="st-toast">{message}</div> : null}

        <div className="settings-layout">
          <aside className="st-nav-card">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`st-nav-item${activeTab === tab.id ? ' active' : ''}`}
                onClick={() => {
                  setActiveTab(tab.id);
                  setErrors({});
                }}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </aside>

          <section className="st-form-card">
            {/* COMPANY */}
            {activeTab === 'company' && (
              <form onSubmit={onSaveCompany}>
                <div className="st-form-head">
                  <span>🏢</span>
                  <h3>Company Details</h3>
                </div>

                <div className="row g-4">
                  <div className="col-12">
                    <label className="form-label">
                      Company Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      value={company.companyName}
                      onChange={handleCompanyChange}
                      className="form-control"
                    />
                    {errors.companyName && (
                      <small className="text-danger">{errors.companyName}</small>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">
                      Company Registration Number <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="registrationNumber"
                      value={company.registrationNumber}
                      onChange={handleCompanyChange}
                      className="form-control"
                    />
                    {errors.registrationNumber && (
                      <small className="text-danger">{errors.registrationNumber}</small>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">
                      Social Security / Tax Number <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="taxNumber"
                      value={company.taxNumber}
                      onChange={handleCompanyChange}
                      className="form-control"
                    />
                    {errors.taxNumber && (
                      <small className="text-danger">{errors.taxNumber}</small>
                    )}
                  </div>

                  <div className="col-12">
                    <label className="form-label">
                      Billing Address <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="billingAddress"
                      value={company.billingAddress}
                      onChange={handleCompanyChange}
                      className="form-control"
                    />
                    {errors.billingAddress && (
                      <small className="text-danger">{errors.billingAddress}</small>
                    )}
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">
                      City <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={company.city}
                      onChange={handleCompanyChange}
                      className="form-control"
                    />
                    {errors.city && <small className="text-danger">{errors.city}</small>}
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">
                      Postal Code <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="postalCode"
                      value={company.postalCode}
                      onChange={handleCompanyChange}
                      className="form-control"
                    />
                    {errors.postalCode && (
                      <small className="text-danger">{errors.postalCode}</small>
                    )}
                  </div>

                  <div className="col-md-4">
                    <label className="form-label">
                      Country <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="country"
                      value={company.country}
                      onChange={handleCompanyChange}
                      className="form-control"
                    />
                    {errors.country && (
                      <small className="text-danger">{errors.country}</small>
                    )}
                  </div>

                  <div className="col-12">
                    <label className="form-label">
                      Agency Type <span className="text-danger">*</span>
                    </label>
                    <select
                      name="agencyType"
                      value={company.agencyType}
                      onChange={handleCompanyChange}
                      className="form-select"
                    >
                      <option value="">Select type...</option>
                      <option value="hotel">Hotel</option>
                      <option value="travel">Travel Agency</option>
                      <option value="corporate">Corporate</option>
                    </select>
                    {errors.agencyType && (
                      <small className="text-danger">{errors.agencyType}</small>
                    )}
                  </div>
                </div>

                <div className="st-actions">
                  <button type="submit" className="btn btn-primary px-4" disabled={saving}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            )}

            {/* CONTACT */}
            {activeTab === 'contact' && (
              <form onSubmit={onSaveContact}>
                <div className="st-form-head">
                  <span>👤</span>
                  <h3>Contact Details</h3>
                </div>

                <div className="row g-4">
                  <div className="col-12">
                    <label className="form-label">
                      Contact Person Full Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="contactPerson"
                      value={contact.contactPerson}
                      onChange={handleContactChange}
                      className="form-control"
                    />
                    {errors.contactPerson && (
                      <small className="text-danger">{errors.contactPerson}</small>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">
                      Email Address <span className="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={contact.email}
                      onChange={handleContactChange}
                      className="form-control"
                    />
                    {errors.email && <small className="text-danger">{errors.email}</small>}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">
                      Phone Number <span className="text-danger">*</span>
                    </label>
                    <PhoneInput
                      international
                      defaultCountry="IS"
                      countries={["IS"]}
                      addInternationalOption={false}
                      value={contact.phone}
                      onChange={(value) => {
                        setContact((prev) => ({ ...prev, phone: value || '' }));
                        clearError('phone');
                      }}
                    />
                    {errors.phone && <small className="text-danger">{errors.phone}</small>}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Job Title</label>
                    <input
                      type="text"
                      name="jobTitle"
                      value={contact.jobTitle}
                      onChange={handleContactChange}
                      className="form-control"
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Website</label>
                    <input
                      type="url"
                      name="website"
                      value={contact.website}
                      onChange={handleContactChange}
                      className="form-control"
                    />
                  </div>

                  <div className="col-12">
                    <label className="form-label">Additional Notes</label>
                    <textarea
                      name="additionalNotes"
                      value={contact.additionalNotes}
                      onChange={handleContactChange}
                      className="form-control"
                      rows="3"
                    />
                  </div>
                </div>

                <div className="st-actions">
                  <button type="submit" className="btn btn-primary px-4" disabled={saving}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            )}

            {/* BILLING */}
            {activeTab === 'billing' && (
              <form onSubmit={onSaveBilling}>
                <div className="st-form-head">
                  <span>💳</span>
                  <h3>Billing Info</h3>
                </div>

                <div className="row g-4">
                  <div className="col-12">
                    <label className="form-label">Bank Name</label>
                    <input
                      type="text"
                      name="bankName"
                      value={billing.bankName}
                      onChange={handleBillingChange}
                      className="form-control"
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Account Name</label>
                    <input
                      type="text"
                      name="accountName"
                      value={billing.accountName}
                      onChange={handleBillingChange}
                      className="form-control"
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Account Number</label>
                    <input
                      type="text"
                      name="accountNumber"
                      value={billing.accountNumber}
                      onChange={handleBillingChange}
                      className="form-control"
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Routing / IFSC</label>
                    <input
                      type="text"
                      name="routingNumber"
                      value={billing.routingNumber}
                      onChange={handleBillingChange}
                      className="form-control"
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Payment Method</label>
                    <select
                      name="paymentMethod"
                      value={billing.paymentMethod}
                      onChange={handleBillingChange}
                      className="form-select"
                    >
                      <option value="bank">Bank Transfer</option>
                      <option value="paypal">PayPal</option>
                      <option value="check">Check</option>
                    </select>
                  </div>
                </div>

                <div className="st-actions">
                  <button type="submit" className="btn btn-primary px-4" disabled={saving}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            )}

            {/* PASSWORD */}
            {activeTab === 'password' && (
              <form onSubmit={handleUpdatePassword}>
                <div className="st-form-head">
                  <span>🔒</span>
                  <h3>Change Password</h3>
                </div>

                <div className="row g-4" style={{ maxWidth: 520 }}>
                  <div className="col-12">
                    <label className="form-label">
                      Current Password <span className="text-danger">*</span>
                    </label>
                    <div className="password-container">
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        name="currentPassword"
                        value={password.currentPassword}
                        onChange={handlePasswordChange}
                        className="form-control"
                      />
                      <img
                        src={showCurrentPassword ? eyeCloseIcon : eyeIcon}
                        alt={showCurrentPassword ? 'Hide password' : 'Show password'}
                        className="password-toggle"
                        onClick={() => setShowCurrentPassword((prev) => !prev)}
                      />
                    </div>
                    {errors.currentPassword && (
                      <small className="text-danger">{errors.currentPassword}</small>
                    )}
                  </div>

                  <div className="col-12">
                    <label className="form-label">
                      New Password <span className="text-danger">*</span>
                    </label>
                    <div className="password-container">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        name="newPassword"
                        value={password.newPassword}
                        onChange={handlePasswordChange}
                        className="form-control"
                      />
                      <img
                        src={showNewPassword ? eyeCloseIcon : eyeIcon}
                        alt={showNewPassword ? 'Hide password' : 'Show password'}
                        className="password-toggle"
                        onClick={() => setShowNewPassword((prev) => !prev)}
                      />
                    </div>
                    {errors.newPassword && (
                      <small className="text-danger">{errors.newPassword}</small>
                    )}
                  </div>

                  <div className="col-12">
                    <label className="form-label">
                      Confirm New Password <span className="text-danger">*</span>
                    </label>
                    <div className="password-container">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        value={password.confirmPassword}
                        onChange={handlePasswordChange}
                        className="form-control"
                      />
                      <img
                        src={showConfirmPassword ? eyeCloseIcon : eyeIcon}
                        alt={showConfirmPassword ? 'Hide password' : 'Show password'}
                        className="password-toggle"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                      />
                    </div>
                    {errors.confirmPassword && (
                      <small className="text-danger">{errors.confirmPassword}</small>
                    )}
                  </div>
                </div>

                <div className="st-actions">
                  <button type="submit" className="btn btn-primary px-4" disabled={saving}>
                    {saving ? 'Updating...' : 'Update Password'}
                  </button>
                </div>
              </form>
            )}

            {/* NOTIFICATIONS */}
            {activeTab === 'notifications' && (
              <form onSubmit={onSaveNotifications}>
                <div className="st-form-head">
                  <span>🔔</span>
                  <h3>Notifications</h3>
                </div>

                <div className="st-toggles">
                  {[
                    { key: 'emailBookings', label: 'Email me about new bookings' },
                    { key: 'emailCommissions', label: 'Email me commission updates' },
                    { key: 'pushUpdates', label: 'Push notifications for trip updates' },
                    { key: 'smsAlerts', label: 'SMS alerts for urgent changes' },
                  ].map((item) => (
                    <label key={item.key} className="st-toggle-row">
                      <span>{item.label}</span>
                      <input
                        type="checkbox"
                        checked={!!notifPrefs[item.key]}
                        onChange={(e) =>
                          setNotifPrefs({ ...notifPrefs, [item.key]: e.target.checked })
                        }
                      />
                    </label>
                  ))}
                </div>

                <div className="st-actions">
                  <button type="submit" className="btn btn-primary px-4" disabled={saving}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};

export default Settings;